import { LitElement, html, css, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import {
  listTeams,
  listMembers,
  changeRole,
  removeMember,
  roleAtLeast,
  ROLES,
  listTeamSecrets,
  setTeamSecret,
  deleteTeamSecret,
  deleteTeam,
  type Team,
  type Member,
  type Role,
  type TeamSecret,
} from "../api/teams.js";
import { listInvites, createInvite, revokeInvite, type Invite } from "../api/invites.js";
import { listTags, createTag, updateTag, deleteTag, type Tag } from "../api/tags.js";
import { listTeamAudit, typeLabel, type AuditEvent } from "../api/audit.js";
import { erasureAvailable } from "../api/account.js";
import { listCalendars } from "../api/calendars.js";
import { detailedError, humanError, parseError } from "../api/errors.js";
import { currentUser } from "../api/auth.js";
import { navigate } from "../nav.js";
import "./stepup-dialog.js";
import {
  toast,
  type BnBadgeVariant,
  type BnColumn,
  type BnRow,
  type BnTagColor,
} from "../components/index.js";

/**
 * sortTags keeps a locally-mutated catalog in the server's order (name,
 * case-insensitively, id breaking ties) so a newly created or renamed tag lands
 * where a reload would put it instead of at the end of the list.
 */
function sortTags(tags: Tag[]): Tag[] {
  return [...tags].sort((a, b) => {
    const an = a.name.toLowerCase();
    const bn = b.name.toLowerCase();
    if (an !== bn) return an < bn ? -1 : 1;
    return a.id < b.id ? -1 : 1;
  });
}

/** Maps a membership role to a badge variant so labels read consistently. */
function roleVariant(role: Role): BnBadgeVariant {
  switch (role) {
    case "owner":
      return "info";
    case "maintainer":
      return "success";
    default:
      return "neutral";
  }
}

/**
 * page-team (/teams/:id) shows a team's roster and lets a maintainer+ manage it:
 * change a member's role and remove a member. Controls the caller's role can't
 * perform are disabled; the server stays the source of truth and its
 * {code,message} outcomes (e.g. the last-owner invariant) surface as toasts.
 *
 * Joining a team goes through the maintainer+ "Invitations" card — the only way
 * to bring someone in, whether or not they already have a bino Cloud account.
 * It holds the invite form plus the pending invites with Resend and Revoke.
 * Resend is a re-create: the server replaces the pending row and mails a fresh
 * token, killing the old link.
 *
 * Maintainers+ also get a collapsible "Audit" card: the team's auth audit feed
 * (GET /v1/teams/{id}/audit), loaded lazily on first expand and paged with a
 * "Load more" button. Below maintainer the card is not rendered at all — same
 * gate as the Invitations card — and the server 403s the route regardless.
 *
 * Owners of a shared team get a "Delete team" card at the bottom when the
 * server offers erasure (the probe in api/account.ts); a personal team goes
 * only with its account, so it never shows the card. The delete asks for a
 * confirm and then a step-up grant bound to this team, and ends on /teams.
 */
@customElement("page-team")
export class PageTeam extends LitElement {
  @property() teamId = "";

  @state() private team: Team | null = null;
  @state() private members: Member[] = [];
  @state() private loading = true;
  @state() private error = "";

  /** userId whose row has an in-flight role change (disables its select). */
  @state() private busyUserId = "";
  @state() private removeTarget: Member | null = null;
  @state() private removing = false;

  // Invitations (maintainer+ only): fetched with the roster when the caller can
  // manage them, so the card is populated before it is first painted. Below
  // maintainer nothing here is rendered or requested.
  @state() private invites: Invite[] = [];
  @state() private invitesLoading = false;
  @state() private invitesError = "";
  @state() private inviteEmail = "";
  @state() private inviteRole: Role = "member";
  @state() private inviting = false;
  @state() private inviteError = "";
  /** Invite id with an in-flight resend/revoke (disables that row's buttons). */
  @state() private busyInviteId = "";

  // Team secrets: names are readable by any member (viewer+), writes need
  // maintainer+. Values are never returned by the API, so there is nothing to
  // hold here but names.
  @state() private secrets: TeamSecret[] = [];
  @state() private secretsLoading = false;
  @state() private secretsError = "";
  @state() private secretName = "";
  @state() private secretValue = "";
  @state() private savingSecret = false;
  @state() private secretError = "";
  @state() private pendingSecretDelete = "";

  // Tag catalog: readable by any member, creatable by member+, and renamed /
  // recolored / deleted only by maintainer+ — those three reach every workspace
  // on the team at once, while a new tag reaches nothing until it is attached.
  @state() private tags: Tag[] = [];
  @state() private tagsLoading = false;
  @state() private tagsError = "";
  @state() private newTagName = "";
  @state() private newTagColor: BnTagColor = "gray";
  @state() private creatingTag = false;
  @state() private tagFormError = "";
  @state() private editTag: Tag | null = null;
  @state() private editTagName = "";
  @state() private editTagColor: BnTagColor = "gray";
  @state() private editTagError = "";
  @state() private savingTag = false;
  @state() private pendingTagDelete: Tag | null = null;
  @state() private deletingTag = false;

  // Audit card (maintainer+): collapsed by default, events fetched lazily on
  // first expand (auditLoaded remembers that so re-expanding doesn't refetch).
  // Separate error/load-more states keep paging failures as toasts.
  @state() private auditOpen = false;
  @state() private auditLoaded = false;
  @state() private auditItems: AuditEvent[] = [];
  @state() private auditLoading = false;
  @state() private auditError = "";
  @state() private auditNextBefore = "";
  @state() private auditMoreLoading = false;

  // Team deletion (owners of a shared team, erasure on): whether the server
  // offers it, the confirm modal, the step-up dialog, and the in-flight flag.
  @state() private erasureOn = false;
  @state() private deleteConfirmOpen = false;
  @state() private deleteVerifyOpen = false;
  @state() private deletingTeam = false;

  /** Whether the server has deployments on; decides the Calendars link. */
  @state() private calendarsOn = false;

  private readonly me = currentUser()?.id ?? "";

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .back {
      display: inline-flex;
      align-items: center;
      gap: var(--bino-space-xs);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      text-decoration: none;
      margin-bottom: var(--bino-space-md);
    }
    .back:hover {
      color: var(--bino-text);
    }
    .head {
      display: flex;
      align-items: flex-start;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
      flex-wrap: wrap;
    }
    .titles {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }
    .spacer {
      flex: 1;
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-lg);
      font-weight: 600;
      color: var(--bino-text);
    }
    .meta {
      display: inline-flex;
      align-items: center;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
    }
    .slug {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }

    /* Add-member form row */
    .add {
      display: flex;
      align-items: flex-end;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
    }
    .add .grow {
      flex: 1 1 16rem;
      min-width: 12rem;
    }
    .add bn-field {
      margin-bottom: 0;
    }

    /* Token-styled native select, matching bn-input */
    select {
      width: 100%;
      box-sizing: border-box;
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
      padding: 0.5rem 0.6875rem;
      border: 1px solid var(--bino-border-light);
      border-radius: var(--bino-radius);
      background: var(--bino-surface-subtle);
      cursor: pointer;
      transition:
        border-color var(--bino-transition-fast),
        box-shadow var(--bino-transition-fast);
    }
    select:focus {
      outline: none;
      border-color: var(--bino-primary);
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }
    select:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    select.role {
      width: auto;
      min-width: 8.5rem;
    }

    /* Roster table */
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
    }
    thead th {
      background: var(--bino-surface-subtle);
      color: var(--bino-text-secondary);
      font-family: var(--bino-font-mono);
      font-weight: 600;
      font-size: var(--bino-font-size-xs);
      letter-spacing: 0.08em;
      text-transform: uppercase;
      text-align: left;
      padding: 0.5rem 0.75rem;
      border-bottom: 1px solid var(--bino-border);
      white-space: nowrap;
    }
    tbody td {
      padding: 0.625rem 0.75rem;
      border-bottom: 1px solid var(--bino-border);
      vertical-align: middle;
    }
    tbody tr:last-child td {
      border-bottom: none;
    }
    td.right {
      text-align: right;
      width: 1px;
      white-space: nowrap;
    }
    .who {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
    }
    .who .name {
      font-weight: 600;
      color: var(--bino-text);
      display: inline-flex;
      align-items: center;
      gap: var(--bino-space-xs);
    }
    .who .email {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .joined {
      font-size: var(--bino-font-size-base);
      color: var(--bino-text-secondary);
      white-space: nowrap;
    }
    .invite-email {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text);
    }
    .row-actions {
      display: flex;
      gap: var(--bino-space-xs);
      justify-content: flex-end;
    }
    .scroll {
      overflow-x: auto;
    }
    .section-gap {
      margin-top: var(--bino-space-lg);
    }

    /* Shared by the lazily-loaded cards (invitations, audit) */
    .load-error {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--bino-space-md);
      padding: var(--bino-space-xl) var(--bino-space-lg);
      text-align: center;
      color: var(--bino-text-secondary);
    }
    .load-more {
      display: flex;
      justify-content: center;
      padding: var(--bino-space-sm) var(--bino-space-md);
      border-top: 1px solid var(--bino-border);
    }
    .card-copy {
      margin: 0 0 var(--bino-space-md);
      max-width: 60ch;
      font-size: var(--bino-font-size-base);
      color: var(--bino-text-secondary);
    }
  `;

  override updated(changed: PropertyValues) {
    if (changed.has("teamId") && this.teamId) void this.load();
  }

  private async load() {
    this.loading = true;
    this.error = "";
    // A teamId change means a different team's feed: drop any loaded audit
    // state so an expand fetches the right team's events.
    this.auditOpen = false;
    this.auditLoaded = false;
    this.auditItems = [];
    this.auditError = "";
    this.auditNextBefore = "";
    this.invites = [];
    this.invitesError = "";
    this.secrets = [];
    this.secretsError = "";
    this.tags = [];
    this.tagsError = "";
    this.erasureOn = false;
    this.calendarsOn = false;
    try {
      const [teams, members] = await Promise.all([listTeams(), listMembers(this.teamId)]);
      const team = teams.find((t) => t.id === this.teamId) ?? null;
      if (!team) {
        this.team = null;
        this.error = "That team does not exist, or you are not a member of it.";
        return;
      }
      this.team = team;
      this.members = members;
      // Pending invites are a maintainer-only surface, and the route 403s below
      // that role — so only ask for them when the card will actually render.
      if (roleAtLeast(team.role, "maintainer")) void this.loadInvites();
      // Secret names are viewer+, so the card renders for every member.
      void this.loadSecrets();
      // The tag catalog is viewer+ too.
      void this.loadTags();
      if (team.role === "owner" && !team.personal) void this.probeErasure(team.id);
      void this.probeCalendars(team.id);
    } catch (err) {
      this.error = humanError(err);
    } finally {
      this.loading = false;
    }
  }

  /**
   * probeCalendars reads the calendars once to learn whether the server has
   * deployments on; a failure keeps the link hidden.
   */
  private async probeCalendars(teamId: string) {
    try {
      const calendars = await listCalendars(teamId);
      if (this.teamId === teamId) this.calendarsOn = calendars !== null;
    } catch {
      /* hidden */
    }
  }

  private async probeErasure(teamId: string) {
    const on = await erasureAvailable();
    // A teamId change while the probe ran belongs to another load.
    if (this.teamId === teamId) this.erasureOn = on;
  }

  /** invariantOr surfaces the server's own message for invariant failures
   *  (e.g. removing/demoting the last owner), else the mapped human message. */
  private invariantOr(err: unknown): string {
    const { code, message } = parseError(err);
    if (code === "invariant_violation") {
      return message && message.trim() !== ""
        ? message
        : "A team must always keep at least one owner.";
    }
    return humanError(err);
  }

  private async onChangeRole(m: Member, role: Role) {
    if (role === m.role) return;
    this.busyUserId = m.userId;
    try {
      const updated = await changeRole(this.teamId, m.userId, role);
      this.members = this.members.map((x) =>
        x.userId === m.userId ? { ...x, role: updated.role } : x,
      );
      toast(`${m.email}: role changed to ${updated.role}`, "success");
    } catch (err) {
      toast(this.invariantOr(err), "error");
      this.requestUpdate(); // revert the select to the unchanged role
    } finally {
      this.busyUserId = "";
    }
  }

  private async doRemove() {
    const m = this.removeTarget;
    if (!m) return;
    this.removing = true;
    try {
      await removeMember(this.teamId, m.userId);
      if (m.userId === this.me) {
        toast("You left the team", "success");
        this.removeTarget = null;
        navigate("/teams");
        return;
      }
      this.members = this.members.filter((x) => x.userId !== m.userId);
      toast(`${m.email} removed`, "success");
      this.removeTarget = null;
    } catch (err) {
      toast(this.invariantOr(err), "error");
    } finally {
      this.removing = false;
    }
  }

  private joined(m: Member): string {
    if (!m.created) return "—";
    const d = new Date(m.created);
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
  }

  // ---- Invitations -----------------------------------------------------------

  private async loadInvites() {
    this.invitesLoading = true;
    this.invitesError = "";
    try {
      this.invites = await listInvites(this.teamId);
    } catch (err) {
      this.invitesError = humanError(err);
    } finally {
      this.invitesLoading = false;
    }
  }

  private async submitInvite() {
    // Enter in the email field and the button are two ways into this, so guard
    // re-entry: a double submit would mail the invitee twice (the second create
    // replaces the first row) — same guard the row actions and accept() use.
    if (this.inviting) return;
    const email = this.inviteEmail.trim();
    if (!email) {
      this.inviteError = "Enter an email address.";
      return;
    }
    this.inviting = true;
    this.inviteError = "";
    try {
      const invite = await createInvite(this.teamId, email, this.inviteRole);
      // Inviting an address that already has a pending invite replaces the row
      // server-side (new token, old link dead), so drop the stale local copy
      // instead of showing the same address twice. Prepended, not appended:
      // the list route sorts newest-first, so appending would put the new row
      // at the bottom until the next load moved it to the top.
      this.invites = [invite, ...this.invites.filter((i) => i.email !== invite.email)];
      toast(`Invitation sent to ${invite.email}`, "success");
      this.inviteEmail = "";
      this.inviteRole = "member";
    } catch (err) {
      this.inviteError = humanError(err);
    } finally {
      this.inviting = false;
    }
  }

  /** Resend re-creates the invite: same address and role, fresh token + email. */
  private async resendInvite(inv: Invite) {
    if (this.busyInviteId !== "") return;
    this.busyInviteId = inv.id;
    try {
      const fresh = await createInvite(this.teamId, inv.email, inv.role);
      this.invites = this.invites.map((i) => (i.id === inv.id ? fresh : i));
      toast(`Invitation resent to ${inv.email}`, "success");
    } catch (err) {
      toast(humanError(err), "error");
    } finally {
      this.busyInviteId = "";
    }
  }

  private async revokeInviteRow(inv: Invite) {
    if (this.busyInviteId !== "") return;
    this.busyInviteId = inv.id;
    try {
      await revokeInvite(this.teamId, inv.id);
      this.invites = this.invites.filter((i) => i.id !== inv.id);
      toast(`Invitation to ${inv.email} revoked`, "success");
    } catch (err) {
      toast(humanError(err), "error");
    } finally {
      this.busyInviteId = "";
    }
  }

  /** expiresIn renders an invite's remaining lifetime ("in 5d"); the server
   *  drops expired rows lazily, so one can briefly show up as expired. */
  private expiresIn(iso: string): string {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const hours = Math.floor((d.getTime() - Date.now()) / 3_600_000);
    if (hours <= 0) return "expired";
    if (hours < 24) return `in ${hours}h`;
    return `in ${Math.floor(hours / 24)}d`;
  }

  // ---- Audit -----------------------------------------------------------------

  private toggleAudit() {
    this.auditOpen = !this.auditOpen;
    if (this.auditOpen && !this.auditLoaded) void this.loadAudit();
  }

  private async loadAudit() {
    this.auditLoading = true;
    this.auditError = "";
    try {
      const page = await listTeamAudit(this.teamId, { limit: 20 });
      this.auditItems = page.items;
      this.auditNextBefore = page.nextBefore;
      this.auditLoaded = true;
    } catch (err) {
      this.auditError = humanError(err);
    } finally {
      this.auditLoading = false;
    }
  }

  private async loadMoreAudit() {
    if (this.auditNextBefore === "" || this.auditMoreLoading) return;
    this.auditMoreLoading = true;
    try {
      const page = await listTeamAudit(this.teamId, { before: this.auditNextBefore, limit: 20 });
      this.auditItems = [...this.auditItems, ...page.items];
      this.auditNextBefore = page.nextBefore;
    } catch (err) {
      // Paging failures toast instead of replacing the card: the rows already
      // on screen are still valid, only the "older" fetch failed.
      toast(humanError(err), "error");
    } finally {
      this.auditMoreLoading = false;
    }
  }

  /** fmtWhen renders a compact relative time ("5m ago") while it is meaningful
   *  and falls back to an absolute date for events older than a week. */
  private fmtWhen(iso: string): string {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const mins = Math.floor((Date.now() - d.getTime()) / 60_000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }

  private renderRoleControl(m: Member, callerRole: Role) {
    // To change a member you need maintainer+ and at least the member's own role.
    const editable = roleAtLeast(callerRole, "maintainer") && roleAtLeast(callerRole, m.role);
    if (!editable) {
      return html`<bn-badge .variant=${roleVariant(m.role)}>${m.role}</bn-badge>`;
    }
    const busy = this.busyUserId === m.userId;
    return html`<select
      class="role"
      aria-label="Role for ${m.email}"
      .value=${m.role}
      ?disabled=${busy}
      @change=${(e: Event) => this.onChangeRole(m, (e.target as HTMLSelectElement).value as Role)}
    >
      ${ROLES.map(
        (r) => html`<option value=${r} ?disabled=${!roleAtLeast(callerRole, r)} ?selected=${r === m.role}>
          ${r}
        </option>`,
      )}
    </select>`;
  }

  private renderRemove(m: Member, callerRole: Role) {
    const self = m.userId === this.me;
    // Self-leave is always allowed; removing others needs maintainer+ and rank.
    const canRemove =
      self || (roleAtLeast(callerRole, "maintainer") && roleAtLeast(callerRole, m.role));
    return html`<bn-button
      variant="danger"
      size="sm"
      ?disabled=${!canRemove}
      @click=${() => (this.removeTarget = m)}
      >${self ? "Leave" : "Remove"}</bn-button
    >`;
  }

  private renderRoster(callerRole: Role) {
    if (this.members.length === 0) {
      return html`<bn-empty-state
        icon="○"
        heading="No members"
        message="This team has no members yet."
      ></bn-empty-state>`;
    }
    return html`<div class="scroll">
      <table>
        <thead>
          <tr>
            <th>Member</th>
            <th>Role</th>
            <th>Joined</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          ${this.members.map(
            (m) => html`<tr>
              <td>
                <span class="who">
                  <span class="name"
                    >${m.name || m.email}
                    ${m.userId === this.me
                      ? html`<bn-badge variant="info">you</bn-badge>`
                      : nothing}</span
                  >
                  <span class="email">${m.email}</span>
                </span>
              </td>
              <td>${this.renderRoleControl(m, callerRole)}</td>
              <td><span class="joined">${this.joined(m)}</span></td>
              <td class="right">${this.renderRemove(m, callerRole)}</td>
            </tr>`,
          )}
        </tbody>
      </table>
    </div>`;
  }

  private renderInviteForm(callerRole: Role) {
    const canInviteOwner = roleAtLeast(callerRole, "owner");
    return html`<div class="add">
      <bn-field
        class="grow"
        label="Invite by email"
        error=${this.inviteError}
        hint="They get an email with a link to join. They don't need an account yet."
      >
        <bn-input
          type="email"
          inputmode="email"
          placeholder="teammate@example.com"
          .value=${this.inviteEmail}
          ?invalid=${this.inviteError !== ""}
          @input=${(e: Event) => (this.inviteEmail = (e.target as HTMLInputElement).value)}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === "Enter") void this.submitInvite();
          }}
        ></bn-input>
      </bn-field>
      <bn-field label="Role">
        <select
          class="role"
          aria-label="Role for invited member"
          .value=${this.inviteRole}
          @change=${(e: Event) => (this.inviteRole = (e.target as HTMLSelectElement).value as Role)}
        >
          ${ROLES.map(
            (r) => html`<option value=${r} ?disabled=${r === "owner" && !canInviteOwner} ?selected=${r === this.inviteRole}>
              ${r}
            </option>`,
          )}
        </select>
      </bn-field>
      <bn-field label=" ">
        <bn-button variant="primary" ?loading=${this.inviting} @click=${() => void this.submitInvite()}
          >Send invitation</bn-button
        >
      </bn-field>
    </div>`;
  }

  private renderInvitesTable(callerRole: Role) {
    if (this.invitesLoading) {
      return html`<bn-spinner block label="Loading invitations…"></bn-spinner>`;
    }
    if (this.invitesError) {
      return html`<div class="load-error">
        <span>${this.invitesError}</span>
        <bn-button variant="secondary" size="sm" @click=${() => void this.loadInvites()}
          >Retry</bn-button
        >
      </div>`;
    }
    if (this.invites.length === 0) {
      return html`<bn-empty-state
        icon="✉"
        heading="No pending invitations"
        message="Invitations you send appear here until they're accepted or revoked."
      ></bn-empty-state>`;
    }
    return html`<div class="scroll">
      <table>
        <thead>
          <tr>
            <th>Invited</th>
            <th>Role</th>
            <th>Expires</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          ${this.invites.map((inv) => {
            // Owner invites are an owner-only lever, same rule the server
            // applies to revoking them — mirror it so the buttons don't lie.
            const manageable = roleAtLeast(callerRole, inv.role);
            const busy = this.busyInviteId === inv.id;
            return html`<tr>
              <td><span class="invite-email">${inv.email}</span></td>
              <td><bn-badge .variant=${roleVariant(inv.role)}>${inv.role}</bn-badge></td>
              <td><span class="joined">${this.expiresIn(inv.expires)}</span></td>
              <td class="right">
                <span class="row-actions">
                  <bn-button
                    variant="secondary"
                    size="sm"
                    ?disabled=${!manageable || busy}
                    @click=${() => void this.resendInvite(inv)}
                    aria-label=${"Resend invitation to " + inv.email}
                    >Resend</bn-button
                  >
                  <bn-button
                    variant="danger"
                    size="sm"
                    ?disabled=${!manageable || busy}
                    @click=${() => void this.revokeInviteRow(inv)}
                    aria-label=${"Revoke invitation to " + inv.email}
                    >Revoke</bn-button
                  >
                </span>
              </td>
            </tr>`;
          })}
        </tbody>
      </table>
    </div>`;
  }

  private renderInvitesSection(callerRole: Role) {
    return html`<bn-card class="section-gap" heading="Invitations">
      ${this.renderInviteForm(callerRole)}
      <div class="section-gap">${this.renderInvitesTable(callerRole)}</div>
    </bn-card>`;
  }

  private renderAuditBody() {
    if (this.auditLoading) {
      return html`<bn-spinner block label="Loading audit events…"></bn-spinner>`;
    }
    if (this.auditError) {
      return html`<div class="load-error">
        <span>${this.auditError}</span>
        <bn-button variant="secondary" size="sm" @click=${() => void this.loadAudit()}
          >Retry</bn-button
        >
      </div>`;
    }
    // Every event a team feed can hold is a membership change, so "which
    // member" is the point of the row — without it "Team member added" three
    // times running says nothing. The roster is already loaded, so the
    // userId→email join is local.
    const emailOf = new Map(this.members.map((m) => [m.userId, m.email]));
    const columns: BnColumn[] = [
      { key: "event", label: "Event" },
      { key: "member", label: "Member" },
      { key: "ip", label: "IP address", mono: true },
      { key: "when", label: "When", align: "right" },
    ];
    // ip may be empty: membership changes recorded via model hooks carry no
    // request context. A removed member is no longer on the roster, so their
    // rows fall back to the raw user id — opaque, but it still ties the
    // add/remove pair for one person together, which "—" would not.
    const rows: BnRow[] = this.auditItems.map((ev) => ({
      event: typeLabel(ev.type),
      member: ev.userId ? (emailOf.get(ev.userId) ?? ev.userId) : "—",
      ip: ev.ip || "—",
      when: this.fmtWhen(ev.created),
    }));
    return html`<bn-table
        .columns=${columns}
        .rows=${rows}
        empty="No audit events yet. Sign-ins and membership changes will show up here."
      ></bn-table>
      ${this.auditNextBefore !== ""
        ? html`<div class="load-more">
            <bn-button
              variant="secondary"
              size="sm"
              ?loading=${this.auditMoreLoading}
              @click=${() => void this.loadMoreAudit()}
              >Load more</bn-button
            >
          </div>`
        : nothing}`;
  }

  // ---- Team secrets ----------------------------------------------------------

  private async loadSecrets() {
    this.secretsLoading = true;
    this.secretsError = "";
    try {
      this.secrets = await listTeamSecrets(this.teamId);
    } catch (err) {
      this.secretsError = humanError(err);
    } finally {
      this.secretsLoading = false;
    }
  }

  private async submitSecret() {
    if (this.savingSecret) return;
    const name = this.secretName.trim();
    if (!name) {
      this.secretError = "Enter a secret name.";
      return;
    }
    if (!this.secretValue) {
      this.secretError = "Enter a value.";
      return;
    }
    this.savingSecret = true;
    this.secretError = "";
    try {
      await setTeamSecret(this.teamId, name, this.secretValue);
      // Saving an existing name rotates it in place, so only new names extend
      // the list. Sorted to match the server's ordering.
      if (!this.secrets.some((s) => s.name === name)) {
        this.secrets = [...this.secrets, { name }].sort((a, b) => (a.name < b.name ? -1 : 1));
      }
      this.secretName = "";
      this.secretValue = "";
      toast(`Secret ${name} saved`, "success");
    } catch (err) {
      // A 400 names the problem, e.g. a name the cluster reserves.
      this.secretError = detailedError(err);
    } finally {
      this.savingSecret = false;
    }
  }

  private async confirmSecretDelete() {
    const name = this.pendingSecretDelete;
    try {
      await deleteTeamSecret(this.teamId, name);
      this.secrets = this.secrets.filter((s) => s.name !== name);
      toast(`Secret ${name} deleted`, "success");
    } catch (err) {
      toast(humanError(err), "error");
    } finally {
      this.pendingSecretDelete = "";
    }
  }

  // ---- Tag catalog ----

  private async loadTags() {
    this.tagsLoading = true;
    this.tagsError = "";
    try {
      this.tags = await listTags(this.teamId);
    } catch (err) {
      this.tagsError = humanError(err);
    } finally {
      this.tagsLoading = false;
    }
  }

  private async submitTag() {
    const name = this.newTagName.trim();
    if (!name) {
      this.tagFormError = "Enter a tag name.";
      return;
    }
    this.creatingTag = true;
    this.tagFormError = "";
    try {
      const tag = await createTag(this.teamId, name, this.newTagColor);
      this.tags = sortTags([...this.tags, tag]);
      this.newTagName = "";
      toast("Tag created", "success");
    } catch (err) {
      this.tagFormError = humanError(err);
    } finally {
      this.creatingTag = false;
    }
  }

  private openEditTag(tag: Tag) {
    this.editTag = tag;
    this.editTagName = tag.name;
    this.editTagColor = tag.color;
    this.editTagError = "";
  }

  private async saveTag() {
    const tag = this.editTag;
    if (!tag) return;
    const name = this.editTagName.trim();
    if (!name) {
      this.editTagError = "Enter a tag name.";
      return;
    }
    this.savingTag = true;
    this.editTagError = "";
    try {
      const updated = await updateTag(this.teamId, tag.id, { name, color: this.editTagColor });
      this.tags = sortTags(this.tags.map((t) => (t.id === updated.id ? updated : t)));
      this.editTag = null;
      toast("Tag updated", "success");
    } catch (err) {
      this.editTagError = humanError(err);
    } finally {
      this.savingTag = false;
    }
  }

  private async confirmDeleteTag() {
    const tag = this.pendingTagDelete;
    if (!tag) return;
    this.deletingTag = true;
    try {
      await deleteTag(this.teamId, tag.id);
      this.tags = this.tags.filter((t) => t.id !== tag.id);
      this.pendingTagDelete = null;
      toast("Tag deleted", "success");
    } catch (err) {
      toast(humanError(err), "error");
    } finally {
      this.deletingTag = false;
    }
  }

  private renderTagForm() {
    return html`<div class="add">
      <bn-field
        class="grow"
        label="Name"
        error=${this.tagFormError}
        hint="Available to every workspace on this team."
      >
        <bn-input
          placeholder="production"
          .value=${this.newTagName}
          ?invalid=${this.tagFormError !== ""}
          @input=${(e: CustomEvent<{ value: string }>) => (this.newTagName = e.detail.value)}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === "Enter") void this.submitTag();
          }}
        ></bn-input>
      </bn-field>
      <bn-field label="Color">
        <bn-color-picker
          label="Tag color"
          .value=${this.newTagColor}
          @change=${(e: CustomEvent<{ value: BnTagColor }>) => (this.newTagColor = e.detail.value)}
        ></bn-color-picker>
      </bn-field>
      <bn-field label=" ">
        <bn-button variant="primary" ?loading=${this.creatingTag} @click=${() => void this.submitTag()}
          >Add tag</bn-button
        >
      </bn-field>
    </div>`;
  }

  private renderTagsTable(callerRole: Role) {
    if (this.tagsLoading && this.tags.length === 0) {
      return html`<bn-spinner block label="Loading tags…"></bn-spinner>`;
    }
    if (this.tagsError) {
      return html`<div class="load-error">
        <span>${this.tagsError}</span>
        <bn-button variant="secondary" size="sm" @click=${() => void this.loadTags()}
          >Retry</bn-button
        >
      </div>`;
    }
    const canManage = roleAtLeast(callerRole, "maintainer");
    const columns: BnColumn[] = [
      { key: "tag", label: "Tag" },
      { key: "actions", label: "", align: "right", width: "1px" },
    ];
    const rows: BnRow[] = this.tags.map((t) => ({
      tag: html`<bn-tag .name=${t.name} color=${t.color}></bn-tag>`,
      // The action pair is laid out with an inline style, not the page's
      // .row-actions class: a cell template is rendered inside bn-table's shadow
      // root, which this page's stylesheet does not reach, so the class would
      // silently do nothing and the two buttons would stack. Inline styles are
      // element-level and cross the boundary; the custom property still resolves
      // because custom properties inherit through it.
      actions: canManage
        ? html`<span
            style="display:flex;gap:var(--bino-space-xs);justify-content:flex-end;white-space:nowrap"
          >
            <bn-button
              size="sm"
              variant="secondary"
              aria-label=${`Edit tag ${t.name}`}
              @click=${() => this.openEditTag(t)}
              >Edit</bn-button
            >
            <bn-button
              size="sm"
              variant="danger"
              aria-label=${`Delete tag ${t.name}`}
              @click=${() => (this.pendingTagDelete = t)}
              >Delete</bn-button
            >
          </span>`
        : nothing,
    }));
    return html`<bn-table
      .columns=${columns}
      .rows=${rows}
      empty="No tags yet. A tag can be attached to any workspace on this team."
    ></bn-table>`;
  }

  private renderTagsSection(callerRole: Role) {
    return html`<bn-card class="section-gap" heading="Tags">
      ${roleAtLeast(callerRole, "member") ? this.renderTagForm() : nothing}
      <div class="section-gap">${this.renderTagsTable(callerRole)}</div>
    </bn-card>`;
  }

  private renderSecretForm() {
    return html`<div class="add">
      <bn-field class="grow" label="Name" error=${this.secretError} hint="An environment variable name, e.g. WAREHOUSE_URL.">
        <bn-input
          placeholder="WAREHOUSE_URL"
          .value=${this.secretName}
          ?invalid=${this.secretError !== ""}
          @input=${(e: Event) => (this.secretName = (e.target as HTMLInputElement).value)}
        ></bn-input>
      </bn-field>
      <bn-field class="grow" label="Value" hint="Write-only. It is never shown again.">
        <bn-input
          type="password"
          .value=${this.secretValue}
          @input=${(e: Event) => (this.secretValue = (e.target as HTMLInputElement).value)}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === "Enter") void this.submitSecret();
          }}
        ></bn-input>
      </bn-field>
      <bn-field label=" ">
        <bn-button variant="primary" ?loading=${this.savingSecret} @click=${() => void this.submitSecret()}
          >Save secret</bn-button
        >
      </bn-field>
    </div>`;
  }

  private renderSecretsTable(callerRole: Role) {
    if (this.secretsLoading && this.secrets.length === 0) {
      return html`<bn-spinner block label="Loading secrets…"></bn-spinner>`;
    }
    if (this.secretsError) {
      return html`<div class="load-error">
        <span>${this.secretsError}</span>
        <bn-button variant="secondary" size="sm" @click=${() => void this.loadSecrets()}
          >Retry</bn-button
        >
      </div>`;
    }
    const canManage = roleAtLeast(callerRole, "maintainer");
    const columns: BnColumn[] = [
      { key: "name", label: "Name", mono: true },
      { key: "actions", label: "", align: "right", width: "1px" },
    ];
    const rows: BnRow[] = this.secrets.map((s) => ({
      name: s.reserved
        ? html`${s.name}
            <bn-badge variant="warning">reserved, builds ignore it</bn-badge>`
        : s.name,
      actions: canManage
        ? html`<bn-button size="sm" variant="danger" @click=${() => (this.pendingSecretDelete = s.name)}
            >Delete</bn-button
          >`
        : nothing,
    }));
    return html`<bn-table
      .columns=${columns}
      .rows=${rows}
      empty="No team secrets yet. A team secret is available to every workspace in the team."
    ></bn-table>`;
  }

  private renderSecretsSection(callerRole: Role) {
    return html`<bn-card class="section-gap" heading="Team secrets">
      ${roleAtLeast(callerRole, "maintainer") ? this.renderSecretForm() : nothing}
      <div class="section-gap">${this.renderSecretsTable(callerRole)}</div>
    </bn-card>`;
  }

  // ---- Team deletion ---------------------------------------------------------

  private continueDeleteTeam() {
    this.deleteConfirmOpen = false;
    this.deleteVerifyOpen = true;
  }

  private async runDeleteTeam(verification: string) {
    this.deleteVerifyOpen = false;
    this.deletingTeam = true;
    try {
      const res = await deleteTeam(this.teamId, verification);
      toast(res.status === "erased" ? "Team deleted" : "Team is being deleted", "success");
      navigate("/teams");
    } catch (err) {
      toast(humanError(err), "error");
      // A missing, expired or spent grant only needs a new check.
      const { code } = parseError(err);
      if (code === "verification_required" || code === "verification_invalid") {
        this.deleteVerifyOpen = true;
      }
    } finally {
      this.deletingTeam = false;
    }
  }

  private renderDeleteSection(team: Team) {
    return html`<bn-card class="section-gap" heading="Delete team">
      <p class="card-copy">
        Deleting <strong>${team.displayName || team.name}</strong> removes its workspaces with
        their builds, deployments and secrets, and its tags and invitations. Every member loses
        access. This cannot be undone.
      </p>
      <bn-button
        variant="danger"
        ?loading=${this.deletingTeam}
        @click=${() => (this.deleteConfirmOpen = true)}
        >Delete team</bn-button
      >
    </bn-card>`;
  }

  private renderAuditSection() {
    return html`<bn-card class="section-gap" flush heading="Audit">
      <bn-button slot="actions" variant="secondary" size="sm" @click=${() => this.toggleAudit()}
        >${this.auditOpen ? "Hide" : "Show"}</bn-button
      >
      ${this.auditOpen ? this.renderAuditBody() : nothing}
    </bn-card>`;
  }

  override render() {
    if (this.loading) {
      return html`<bn-spinner block label="Loading team…"></bn-spinner>`;
    }
    if (this.error || !this.team) {
      return html`<bn-empty-state
        icon="⚠"
        heading="Couldn’t load team"
        message=${this.error || "Team not found."}
      >
        <bn-button slot="actions" variant="secondary" @click=${() => navigate("/teams")}
          >Back to teams</bn-button
        >
      </bn-empty-state>`;
    }

    const team = this.team;
    const callerRole = team.role;
    return html`
      <a class="back" href="/teams">← Teams</a>
      <div class="head">
        <div class="titles">
          <h1>${team.displayName || team.name}</h1>
          <div class="meta">
            ${(team.displayName || team.name) !== team.name
              ? html`<span class="slug">${team.name}</span>`
              : nothing}
            ${team.personal ? html`<bn-badge variant="neutral">personal</bn-badge>` : nothing}
            <bn-badge .variant=${roleVariant(callerRole)}>you are ${callerRole}</bn-badge>
          </div>
        </div>
        ${this.calendarsOn
          ? html`<span class="spacer"></span>
              <bn-button
                variant="secondary"
                size="sm"
                @click=${() => navigate(`/teams/${encodeURIComponent(team.id)}/calendars`)}
                >Calendars</bn-button
              >`
          : nothing}
      </div>

      <bn-card heading="Members">
        ${this.renderRoster(callerRole)}
      </bn-card>

      ${roleAtLeast(callerRole, "maintainer") ? this.renderInvitesSection(callerRole) : nothing}
      ${this.renderSecretsSection(callerRole)} ${this.renderTagsSection(callerRole)}
      ${roleAtLeast(callerRole, "maintainer") ? this.renderAuditSection() : nothing}
      ${this.erasureOn ? this.renderDeleteSection(team) : nothing}

      <bn-modal
        ?open=${this.editTag !== null}
        heading="Edit tag"
        @close=${() => (this.editTag = null)}
      >
        <p>Renaming or recoloring updates the tag on every workspace that carries it.</p>
        <bn-field label="Name" required error=${this.editTagError}>
          <bn-input
            .value=${this.editTagName}
            ?invalid=${this.editTagError !== ""}
            @input=${(e: CustomEvent<{ value: string }>) => (this.editTagName = e.detail.value)}
            @keydown=${(e: KeyboardEvent) => {
              if (e.key === "Enter") void this.saveTag();
            }}
          ></bn-input>
        </bn-field>
        <bn-field label="Color">
          <bn-color-picker
            label="Tag color"
            .value=${this.editTagColor}
            @change=${(e: CustomEvent<{ value: BnTagColor }>) =>
              (this.editTagColor = e.detail.value)}
          ></bn-color-picker>
        </bn-field>
        <bn-button slot="footer" variant="ghost" @click=${() => (this.editTag = null)}
          >Cancel</bn-button
        >
        <bn-button
          slot="footer"
          variant="primary"
          ?loading=${this.savingTag}
          @click=${() => void this.saveTag()}
          >Save</bn-button
        >
      </bn-modal>

      <bn-modal
        ?open=${this.pendingTagDelete !== null}
        heading="Delete tag?"
        @close=${() => (this.pendingTagDelete = null)}
      >
        ${this.pendingTagDelete
          ? html`<p>
              Delete <strong>${this.pendingTagDelete.name}</strong>? It is removed from every
              workspace on this team that carries it. The workspaces themselves are untouched.
            </p>`
          : nothing}
        <bn-button slot="footer" variant="ghost" @click=${() => (this.pendingTagDelete = null)}
          >Cancel</bn-button
        >
        <bn-button
          slot="footer"
          variant="danger"
          ?loading=${this.deletingTag}
          @click=${() => void this.confirmDeleteTag()}
          >Delete</bn-button
        >
      </bn-modal>

      <bn-modal
        ?open=${this.removeTarget !== null}
        heading=${this.removeTarget?.userId === this.me ? "Leave team?" : "Remove member?"}
        @close=${() => (this.removeTarget = null)}
      >
        ${this.removeTarget
          ? html`<p>
              ${this.removeTarget.userId === this.me
                ? html`You’ll lose access to <strong>${team.displayName || team.name}</strong> and
                    its workspaces.`
                : html`Remove <strong>${this.removeTarget.email}</strong> from
                    <strong>${team.displayName || team.name}</strong>? They’ll lose access
                    immediately.`}
            </p>`
          : nothing}
        <bn-button slot="footer" variant="ghost" @click=${() => (this.removeTarget = null)}
          >Cancel</bn-button
        >
        <bn-button
          slot="footer"
          variant="danger"
          ?loading=${this.removing}
          @click=${() => this.doRemove()}
          >${this.removeTarget?.userId === this.me ? "Leave team" : "Remove"}</bn-button
        >
      </bn-modal>

      <bn-modal
        ?open=${this.deleteConfirmOpen}
        heading="Delete team?"
        @close=${() => (this.deleteConfirmOpen = false)}
      >
        <p>
          Delete <strong>${team.displayName || team.name}</strong> and all its workspaces for every
          member? Copies in backups are removed within 35 days.
        </p>
        <bn-button slot="footer" variant="ghost" @click=${() => (this.deleteConfirmOpen = false)}
          >Cancel</bn-button
        >
        <bn-button slot="footer" variant="danger" @click=${() => this.continueDeleteTeam()}
          >Delete team</bn-button
        >
      </bn-modal>

      <stepup-dialog
        ?open=${this.deleteVerifyOpen}
        action="delete_team"
        .target=${team.id}
        @verified=${(e: CustomEvent<{ verification: string }>) =>
          void this.runDeleteTeam(e.detail.verification)}
        @close=${() => (this.deleteVerifyOpen = false)}
      ></stepup-dialog>

      <bn-modal
        ?open=${this.pendingSecretDelete !== ""}
        heading="Delete team secret?"
        @close=${() => (this.pendingSecretDelete = "")}
      >
        <p>
          Delete <strong>${this.pendingSecretDelete}</strong>? Builds in every workspace of this team
          will stop receiving it, unless the workspace defines its own.
        </p>
        <bn-button slot="footer" variant="ghost" @click=${() => (this.pendingSecretDelete = "")}
          >Cancel</bn-button
        >
        <bn-button slot="footer" variant="danger" @click=${() => void this.confirmSecretDelete()}
          >Delete</bn-button
        >
      </bn-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-team": PageTeam;
  }
}
